import { BackupRunList, BackupStatusResponse } from '@deizi/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export const backupsKey = ['backups'] as const;

/** Backup status; polls every 2 s while a run is active. */
export function useBackupStatus() {
  return useQuery({
    queryKey: [...backupsKey, 'status'],
    queryFn: () => api.get('/backups/status', BackupStatusResponse),
    refetchInterval: (q) => (q.state.data?.running ? 2000 : 30_000),
    // Keep following a running backup even when the tab is in the background.
    refetchIntervalInBackground: true,
  });
}

/** Recent runs; `version` (from the status) refetches them when a run starts or finishes. */
export function useBackupRuns(version: string) {
  return useQuery({
    queryKey: [...backupsKey, 'runs', version],
    placeholderData: (prev) => prev,
    queryFn: () => api.get('/backups/runs?limit=10', BackupRunList),
  });
}

/** 18_000_000 → "18 MB" (pt-BR, base 1000 like disk tools). */
export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  const digits = v < 10 && i > 0 ? 1 : 0;
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: digits })} ${units[i]}`;
}

/** 9.2 → "9 h", 57 → "57 horas", 0.4 → "menos de 1 hora", 80 → "3 dias". */
export function formatAge(hours: number): string {
  if (hours < 1) return 'menos de 1 hora';
  if (hours < 24) return `${Math.floor(hours)} h`;
  if (hours < 72) return `${Math.floor(hours)} horas`;
  return `${Math.floor(hours / 24)} dias`;
}
